/**
 * Email utility functions
 * Centralized helper functions for email operations
 */

/**
 * Checks if a folder is a folders tab (not settings)
 * @param {string} activeTab - Current active tab
 * @param {Array} foldersTabs - Array of folder tab configurations
 * @returns {boolean}
 */
export const isFoldersTab = (activeTab, foldersTabs) => {
  return foldersTabs?.some(tab => tab?.value === activeTab) || false;
};

/**
 * Checks if a folder is a settings tab
 * @param {string} activeTab - Current active tab
 * @param {Array} settingsTabs - Array of settings tab configurations
 * @returns {boolean}
 */
export const isSettingsTab = (activeTab, settingsTabs) => {
  return settingsTabs?.some(tab => tab?.value === activeTab) || false;
};

/**
 * Determines if emails should be displayed based on current state
 * @param {number} currentPage - Current page number
 * @param {number|null} displayedPage - Page number that has been displayed
 * @returns {boolean}
 */
export const shouldShowMails = (currentPage, displayedPage) => {
  // Show content if data has been loaded (even if empty, to show empty state)
  return displayedPage !== null && displayedPage === currentPage;
};

/**
 * Gets emails for a specific folder from the store
 * @param {Object} store - Redux store state
 * @param {string} activeTab - Current active folder
 * @returns {Array} Array of emails
 */
export const getFolderEmails = (store, activeTab) => {
  return store?.[activeTab] || [];
};

/**
 * Checks if all emails on current page are selected
 * @param {Array} selectedMails - Currently selected emails
 * @param {Array} mails - All emails on current page
 * @returns {boolean}
 */
export const isAllSelected = (selectedMails, mails) => {
  return selectedMails?.length > 0 && selectedMails?.length === mails?.length;
};

/**
 * Validates email address format
 * @param {string} email - Email address to validate
 * @returns {boolean}
 */
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Formats email list for display
 * @param {Array|string} emails - Email addresses
 * @returns {Array} Formatted email array
 */
export const formatEmailList = (emails) => {
  if (!emails) return [];
  if (Array.isArray(emails)) return emails;
  if (typeof emails === 'string') {
    return emails.split(',').map(e => e.trim()).filter(Boolean);
  }
  return [];
};

/**
 * Gets unique email addresses from multiple arrays
 * @param {...Array} emailArrays - Multiple email arrays
 * @returns {Array} Unique email addresses
 */
export const getUniqueEmails = (...emailArrays) => {
  const allEmails = emailArrays.flat().filter(Boolean);
  return [...new Set(allEmails)];
};

/** `=?charset?B|Q?text?=` — an RFC 2047 encoded word, optionally with a `*language` suffix. */
const ENCODED_WORD = /=\?([^?]+)\?([BbQq])\?([^?]*)\?=/g;

const base64ToBytes = (text) =>
  Uint8Array.from(atob(text.replace(/\s+/g, '')), char => char.charCodeAt(0));

const quotedPrintableToBytes = (text) => {
  const chars = text.replace(/_/g, ' ');
  const bytes = [];
  for (let i = 0; i < chars.length; i++) {
    if (chars[i] === '=' && /^[0-9a-f]{2}$/i.test(chars.substr(i + 1, 2))) {
      bytes.push(parseInt(chars.substr(i + 1, 2), 16));
      i += 2;
    } else {
      bytes.push(chars.charCodeAt(i));
    }
  }
  return Uint8Array.from(bytes);
};

/**
 * Decodes RFC 2047 encoded words so non-ASCII headers read as text instead of
 * `=?windows-1256?B?...?=`. Anything it cannot decode is left untouched.
 * @param {string} value - Raw header value
 * @returns {string}
 */
export const decodeMimeWords = (value) => {
  const text = String(value ?? '');
  if (!text.includes('=?')) return text;

  // Whitespace between two adjacent encoded words is header folding, not content.
  return text
    .replace(/\?=\s+=\?/g, '?==?')
    .replace(ENCODED_WORD, (match, charset, encoding, encodedText) => {
      try {
        const bytes = encoding.toUpperCase() === 'B'
          ? base64ToBytes(encodedText)
          : quotedPrintableToBytes(encodedText);
        const label = charset.split('*')[0].toLowerCase();
        try {
          return new TextDecoder(label).decode(bytes);
        } catch {
          return new TextDecoder('utf-8').decode(bytes);
        }
      } catch {
        return match;
      }
    });
};

/**
 * Decodes the headers the UI renders on a mail coming back from the API.
 * @param {Object} mail - Mail object from the web-mail endpoints
 * @returns {Object} Mail with readable headers
 */
export const decodeMailHeaders = (mail) => {
  if (!mail || typeof mail !== 'object') return mail;
  return { ...mail, subject: decodeMimeWords(mail?.subject) };
};

